import { Capacitor } from '@capacitor/core';
import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  useParentTheme,
} from '../../contexts/ParentThemeContext';

import {
  useAuth,
} from '../../contexts/AuthContext';

import {
  useFamilyStatus,
} from '../../contexts/FamilyStatusContext';

import FamilyInactiveBanner
  from '../Family/FamilyInactiveBanner';
import SubscriptionStatusBanner
  from '../Family/SubscriptionStatusBanner';
import MyPiggyIcon from '../UI/MyPiggyIcon';
import ParentNotificationBell from '../Notifications/ParentNotificationBell';


const SUBSCRIPTION_STATUS = {
  trial: {
    label: 'Free Trial',
    className:
      'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300',
  },
  active: {
    label: 'Active',
    className:
      'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300',
  },
  grace: {
    label: 'Grace Period',
    className:
      'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  },
  expired: {
    label: 'Read Only',
    className:
      'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300',
  },
};


const AppLayout = ({
  children,
  title,
  titleAccessory,
  className = '',
  showBack = false,
  onBack,
  showSettings = true,
  showNotifications = false,
}) => {

  const navigate =
    useNavigate();


  const location =
    useLocation();


  const isParentSettingsPage =
    location.pathname ===
      '/parent/settings';

  const {
    isDark,
  } = useParentTheme();


  const {
    parent,
  } = useAuth();

  const isNativeParent = Capacitor.isNativePlatform() && location.pathname.startsWith('/parent') && Boolean(parent);

  // Shared by the Android and iOS Parents shells.
  const tabs = [
    { label: 'Home', path: '/parent', icon: 'family', active: location.pathname === '/parent' },
    { label: 'Kids', path: '/parent/children', icon: 'kid-profile', active: location.pathname.startsWith('/parent/children') },
    { label: 'Chores', path: '/parent/chores/manage', icon: 'do-chores', active: location.pathname.startsWith('/parent/chores') },
    { label: 'Activity', path: '/parent/transactions', icon: 'transaction-history', active: location.pathname === '/parent/transactions' },
    { label: 'Settings', path: '/parent/settings', icon: 'settings', active: !['/parent', '/parent/transactions'].includes(location.pathname) && !location.pathname.startsWith('/parent/children') && !location.pathname.startsWith('/parent/chores') },
  ];



  const {
    isFamilyInactive,
    status: familyStatus,
    ownerName,
    deletionScheduledFor,
    isAdminDeletionScheduled,
    effectiveStatus,
    trialEndsAt,
    graceEndsAt,
  } = useFamilyStatus();


  const subscriptionStatus =
    !isFamilyInactive
      ? SUBSCRIPTION_STATUS[effectiveStatus]
      : null;


  const isOwner =
    parent?.role ===
      'owner';


  const subscriptionHelpText =
    isOwner
      ? 'You can manage your MyPiggy plan, billing cycle, payment method, and cancellation options in Parent Settings.'
      : 'You can view the family\'s subscription status, but only the Family Organizer can manage the plan and payment details.';


  return (
    <div
      className={
        isDark
          ? `dark ${className}`
          : className
      }
    >

      {isNativeParent && Capacitor.getPlatform() === 'ios' && createPortal(
        <div
          aria-hidden="true"
          className="native-parent-status-cover"
          style={{ backgroundColor: isDark ? '#0f172a' : '#ffffff' }}
        />,
        document.body
      )}

      <div className={`parent-theme min-h-screen transition-colors duration-200 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 ${isNativeParent ? 'native-parent-shell' : ''}`}>

        <header className="bg-white dark:bg-slate-900 shadow-sm border-b border-gray-200 dark:border-slate-700 transition-colors duration-200">

          <div className="max-w-4xl mx-auto px-4 py-4">

            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:gap-3">

              <div className="min-w-10 sm:min-w-24">

                {showBack && (

                  <button
                    type="button"
                    onClick={
                      onBack
                    }
                    className="flex items-center text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    <span className="mr-1 sm:mr-2">
                      ←
                    </span>

                    <span className="hidden sm:inline">
                      Back
                    </span>
                  </button>

                )}

              </div>


              <div className="flex min-w-0 items-center justify-center gap-2">
              <h1
                className="min-w-0 px-1 text-center text-lg sm:text-xl leading-tight font-bold text-gray-900 dark:text-slate-100 break-words overflow-hidden [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]"
                title={title}
              >
                {title}
              </h1>
              {titleAccessory}
              </div>


              <div className="flex min-w-10 sm:min-w-24 items-center justify-end gap-2">

                {subscriptionStatus && !isNativeParent && (
                  <div className="flex items-center gap-1.5">
                    <span
                      title={`MyPiggy subscription status: ${subscriptionStatus.label}`}
                      aria-label={`MyPiggy subscription status: ${subscriptionStatus.label}`}
                      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${subscriptionStatus.className}`}
                    >
                      {subscriptionStatus.label}
                    </span>

                    <details className="group relative">
                      <summary
                        aria-label="About MyPiggy subscription access"
                        title="About MyPiggy subscription access"
                        className="flex h-6 w-6 cursor-pointer list-none items-center justify-center rounded-full border border-gray-300 dark:border-slate-600 text-xs font-bold text-gray-600 dark:text-slate-300 transition hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 [&::-webkit-details-marker]:hidden"
                      >
                        i
                      </summary>

                      <div
                        role="note"
                        className="absolute right-0 z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 text-left text-sm font-normal leading-5 text-gray-700 dark:text-slate-200 shadow-lg"
                      >
                        {subscriptionHelpText}
                      </div>
                    </details>
                  </div>
                )}

                {showNotifications && parent?.id && (
                  <ParentNotificationBell parentId={parent.id} />
                )}

                {showSettings && !isNativeParent && (
                <button
                  type="button"
                  data-tour="parent-settings"
                  onClick={() =>
                    navigate(
                      '/parent/settings'
                    )
                  }
                  aria-label="Open parent settings"
                  aria-current={
                    isParentSettingsPage
                      ? 'page'
                      : undefined
                  }
                  title="Parent Settings"
                  className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-xl transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                    isParentSettingsPage
                      ? 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <MyPiggyIcon name="settings" size="sm" />
                </button>
                )}

              </div>

            </div>

          </div>

        </header>


        <main className={`max-w-4xl mx-auto px-4 py-6 text-gray-900 dark:text-slate-100 ${isNativeParent ? 'native-parent-main' : ''}`}>

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.3,
            }}
          >

            {isFamilyInactive && (

              <div className="mb-6">

                <FamilyInactiveBanner
                  status={familyStatus}
                  ownerName={
                    ownerName
                  }
                  deletionScheduledFor={
                    deletionScheduledFor
                  }
                  isAdminDeletionScheduled={isAdminDeletionScheduled}
                />

              </div>

            )}


            {!isFamilyInactive && (

              <SubscriptionStatusBanner
                audience="parent"
                effectiveStatus={
                  effectiveStatus
                }
                trialEndsAt={
                  trialEndsAt
                }
                graceEndsAt={
                  graceEndsAt
                }
                ownerName={
                  ownerName
                }
                canManageBilling={
                  isOwner
                }
                className="mb-6"
              />

            )}


            {children}

          </motion.div>

        </main>

        {isNativeParent && (
          <nav className="native-parent-nav" aria-label="Parent navigation">
            {tabs.map(({ label, path, icon, active }) => (
              <button
                key={label}
                type="button"
                onClick={() => navigate(path)}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                className={`native-parent-tab ${active ? 'native-parent-tab-active' : ''}`}
              >
                <MyPiggyIcon name={icon} size="sm" loading="eager" />
                <span className="text-[11px] font-bold">{label}</span>
              </button>
            ))}
          </nav>
        )}

      </div>

    </div>
  );
};


export default AppLayout;


